import qs from 'querystring';

import ApiService from 'services/ApiService/ApiService';
import ConfigurationService, { ConfigurationKeys } from 'services/ConfigurationService';
import FirebaseService from 'services/FirebaseService';
import { translate } from 'services/i18nService';
import {
  ACCEPT_PRIVATE_SHOW_ERROR,
  BROADCASTER_PRIVATE_SHOW_SETTINGS,
  BROADCASTER_PS_RATINGS,
  DISABLE_GAME,
  SET_IS_PRIVATE_SHOW_REQUESTING_TO_END,
  PRIVATE_SHOW_USER_NOT_IN_ROOM,
  PROFILE_INFO_RESPONSE,
  SET_ACCEPTED_PENDING_USER_FOR_PRIVATE_SHOW,
  SET_ORIGINAL_USERNAME_INSIDE_PRIVATE_SHOW,
} from 'services/MessagingService/EventNames';
import getMessagingServiceInstance, { MessagingService } from 'services/MessagingService/MessagingService';
import ModalService from 'services/ModalService';
import NotificationService from 'services/NotificationService';
import { RATING_TYPE } from 'enum/helpers';
import { PRIVATE_SHOW_ACTIONS } from 'utils/consts';
import parseCatch from 'utils/parseCatch';
import { User } from 'stores/UserStore/UserStore.types';

import firebaseNode from '../firebase/firebaseNode';


const WEBCHAT_HOST = ConfigurationService.get(ConfigurationKeys.WEB_CHAT_HOST);
const BASE_URL = ConfigurationService.get(ConfigurationKeys.BASE_URL);

const GET_PROFILE_INFO_URL = `${BASE_URL}/rest/v1.0/profile/{username}/info`;
const PS_SETTINGS_URL = `${WEBCHAT_HOST}privateShowAction`;
const PS_RATINGS_URL = `${BASE_URL}/rest/v1.0/users/{username}/private-show-ratings`;
const UPDATE_PS_RATINGS_URL = `${BASE_URL}/rest/v1.0/users/{username}/private-show-ratings/{id}`;


type PrivateShowSettings = {
  acceptPS: boolean;
  accessHash: string;
  action: string;
  allowC2C: boolean;
  allowSpy: boolean;
  blocksOfTime: string;
  blocksOfTimeActive: boolean;
  c2cAble: boolean;
  c2cPPM: number;
  psInitialChargeMinutes: number;
  roomname: string;
  spyPPM: number;
  username: string
  viewerPPM: number;
}

export class BroadcasterPrivateShowService {

  constructor(public messagingService: MessagingService) {}

  updatePSSettings = (data: PrivateShowSettings, broadcasterInSoloPS: boolean): void => {
    ApiService.post(PS_SETTINGS_URL, {
      data: qs.stringify(data),
      headers: { 'content-type': 'application/x-www-form-urlencoded' },
    })
      .then((res) => {
        if (res.data.status === 'success' && !broadcasterInSoloPS) {
          NotificationService.create({
            type: NotificationService.type.TOAST,
            subtype: NotificationService.subtype.SUCCESS,
            text: translate('v2broadcast.changes.saved'),
          });
        }
      })
      .catch((error) => {
        parseCatch(error);
        NotificationService.create({
          type: NotificationService.type.TOAST,
          text: translate('account.errorGeneric'),
          subtype: NotificationService.subtype.ERROR,
        });
      });
  };

  getBroadcasterPrivateshowSettings = (data: {
    accessHash: string;
    action: string;
    roomname: string;
    username: User['username'];
  }): void => {
    ApiService.get(PS_SETTINGS_URL, { params: data })
      .then((res) => {
        this.messagingService.emit(BROADCASTER_PRIVATE_SHOW_SETTINGS, res.data.settings);
      })
      .catch((error) => parseCatch(error));
  };

  getBroadcasterPrivateshowRatings = (broadcasterUsername: string, billedUsername: string): void => {
    const encodedUrl = encodeURI(PS_RATINGS_URL.replace('{username}', billedUsername));
    ApiService.get(encodedUrl, {
      params: {
        voterUsername: broadcasterUsername,
        ratingType: RATING_TYPE.VIEWER,
      },
    })
      .then((res) => this.messagingService.emit(BROADCASTER_PS_RATINGS, res.data))
      .catch((error) => parseCatch(error));
  };

  addBroadcasterPrivateshowRating = (
    broadcasterUsername: string,
    accessHash: string,
    billedUsername: string,
    rate: string
  ): void => {
    const encodedUrl = encodeURI(PS_RATINGS_URL.replace('{username}', billedUsername));
    const params = {
      'login_user_name': broadcasterUsername,
      accessHash,
    };
    const data = {
      voterUsername: broadcasterUsername,
      ratingType: RATING_TYPE.VIEWER,
      rating: rate,
    };
    ApiService.post(encodedUrl, { params: qs.stringify(params), data })
      .then(() => ModalService.hideModal())
      .catch((error) => parseCatch(error));
  };

  updateBroadcasterPrivateshowRating = (
    broadcasterUsername: string,
    accessHash: string,
    billedUsername: string,
    ratingId: string,
    rate: string
  ): void => {
    const updateUrl = encodeURI(UPDATE_PS_RATINGS_URL.replace('{username}', billedUsername).replace('{id}', ratingId));
    const params = {
      'login_user_name': broadcasterUsername,
      accessHash,
    };

    ApiService.patch(updateUrl, { params: qs.stringify(params), data: { rating: rate } })
      .then(() => ModalService.hideModal())
      .catch((error) => parseCatch(error));
  };

  /** Firebase listener for added ps requests */
  privateShowRequestAdded = (
    username: User['username'],
    callbackFnc: (snapshot: any) => void
  ) => {
    const privateShowRequestRef = FirebaseService.FirebaseMain.child(firebaseNode.PRIVATE_SHOW_REQUESTS).child(
      username.toLowerCase()
    );

    return privateShowRequestRef.on('child_added', (snapshot: any) => {
      callbackFnc(snapshot);
    });
  };

  /** Firebase listener for removed ps requests */
  privateShowRequestRemoved = (
    username: User['username'],
    callbackFnc: (snapshot: any) => void
  ): any => {
    const privateShowRequestRef = FirebaseService.FirebaseMain.child(firebaseNode.PRIVATE_SHOW_REQUESTS).child(
      username.toLowerCase()
    );

    return privateShowRequestRef.on('child_removed', (snapshot: any) => callbackFnc(snapshot));
  };

  /**
   * Method for getting profile info - needed for ps rating
   * @emits PROFILE_INFO_RESPONSE handles response
   */
  getProfileInfo = (username: User['username']): void => {
    const URL = GET_PROFILE_INFO_URL.replace('{username}', encodeURIComponent(username));

    ApiService.get(URL)
      .then((res) =>
        this.messagingService.emit(
          PROFILE_INFO_RESPONSE,
          { username, rating: res.data.psViewerRating })
      )
      .catch((error) => parseCatch(error));
  };

  /** Method for accepting private show request */
  acceptPrivateShow = (
    request: Partial<{
      billedUsername: string;
      username: string;
      accessHash: string;
    }> = {},
    cb: () => void
  ): void => {
    const { billedUsername, username, accessHash } = request;
    const payload = {
      billedUsername,
      action: PRIVATE_SHOW_ACTIONS.START_PS,
      accessHash,
      username,
      roomname: username,
    };

    this.messagingService.emit(SET_ACCEPTED_PENDING_USER_FOR_PRIVATE_SHOW, billedUsername);

    ApiService.post(PS_SETTINGS_URL, {
      data: qs.stringify(payload),
      headers: { 'content-type': 'application/x-www-form-urlencoded' },
    })
      .then((res) => {
        if (res.data.message === 'PRIVATE_SHOW_REQUEST_USER_NOT_IN_ROOM') {
          ModalService.showModal(ModalService.MODAL_KEYS.ERROR_MODAL, {
            title: translate('v2broadcast.ps.error.user.left.title'),
            text: translate('v2broadcast.ps.error.user.left'),
          });
          this.messagingService.emit(PRIVATE_SHOW_USER_NOT_IN_ROOM, billedUsername);
          cb?.();

          return;
        }
        this.messagingService.emit(DISABLE_GAME);
        this.messagingService.emit(SET_ORIGINAL_USERNAME_INSIDE_PRIVATE_SHOW, billedUsername);
        cb?.();
      })
      .catch((error) => {
        parseCatch(error);
        this.messagingService.emit(ACCEPT_PRIVATE_SHOW_ERROR);
      })
      .finally(() => {
        this.messagingService.emit(SET_ACCEPTED_PENDING_USER_FOR_PRIVATE_SHOW, '');
      });
  };

  /** Method for ending private show */
  endPrivateShow = (username: User['username'], accessHash: string): void => {
    const payload = {
      action: PRIVATE_SHOW_ACTIONS.STOP_PS,
      accessHash,
      username,
      roomname: username,
    };

    this.messagingService.emit(SET_IS_PRIVATE_SHOW_REQUESTING_TO_END, true);

    ApiService.post(PS_SETTINGS_URL, {
      data: qs.stringify(payload),
      headers: { 'content-type': 'application/x-www-form-urlencoded' },
    })
      .then(ModalService.hideModal)
      .catch(parseCatch)
      .finally(() =>
        this.messagingService.emit(SET_IS_PRIVATE_SHOW_REQUESTING_TO_END, false)
      );
  };
}

export default new BroadcasterPrivateShowService(getMessagingServiceInstance());
